import { convexQuery } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import { useQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

const formatDurationSec = (durationSec: number): string => {
  const minPart = Math.floor(durationSec / 60)
  const secPart = durationSec % 60
  return `${minPart}m ${secPart}s`
}

const formatTimestamp = (ms: number): string =>
  new Date(ms).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

export const PublicReportPage = () => {
  const { t } = useTranslation()
  const { shareToken } = useParams({ strict: false }) as { shareToken: string }

  const { data: interview, isLoading } = useQuery(
    convexQuery(api.features.interviews.report_sharing.getReportByShareToken, {
      shareToken,
    }),
  )

  if (isLoading) return null

  if (!interview) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <div className="flex max-w-sm flex-col items-center gap-3 text-center">
          <h2 className="text-xl font-medium text-primary">
            {t('interviews.publicReport.notFound')}
          </h2>
          <p className="text-sm text-primary/60">
            {t('interviews.publicReport.notFoundDescription')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen w-full flex-col px-6 py-8">
      <div className="mx-auto flex w-full max-w-(--breakpoint-xl) flex-col gap-6">
        <div className="flex items-center justify-end gap-3">
          {interview.completedAt !== undefined && (
            <span className="text-sm text-primary/50">
              {t('interviews.report.completedAt')}:{' '}
              {formatTimestamp(interview.completedAt)}
            </span>
          )}
          {interview.durationSec !== undefined && (
            <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-primary/70">
              {formatDurationSec(interview.durationSec)}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-6">
          <section className="flex flex-col gap-3">
            <h3 className="text-base font-semibold text-primary">
              {t('interviews.report.summary')}
            </h3>
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <p className="text-sm leading-relaxed text-primary/80">
                {interview.summary ?? t('interviews.report.noSummary')}
              </p>
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <h3 className="text-base font-semibold text-primary">
              {t('interviews.report.transcript')}
            </h3>
            {interview.transcript ? (
              <pre className="max-h-96 overflow-y-auto rounded-lg border border-border bg-muted/30 p-4 text-sm leading-relaxed whitespace-pre-wrap text-primary/80">
                {interview.transcript}
              </pre>
            ) : (
              <p className="text-sm text-primary/40">
                {t('interviews.report.noTranscript')}
              </p>
            )}
          </section>
        </div>
      </div>
    </div>
  )
}
